import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';
import { useBrand } from '../BrandContext';
import { useAuth } from '../AuthContext';
import {
  ST_COLORS,
  STATUS_ORDER,
  fmt,
  stLabel,
  projLabel,
  sortProjects,
} from '../components/UnitsDeals/constants';
import useIsMobile from '../components/UnitsDeals/useIsMobile';
import { brokerSubmitDeal, brokerDealRequests } from '../api';

// ── BrokerPortal ──
// Partner-facing inventory portal. Shows all projects with availability
// rollups, then drills down to a unit list per project. Generic copy via
// BrandContext so it works for any developer using the platform.
// No client identities, no closers, no commissions — by design.
export default function BrokerPortal() {
  const { t, lang } = useLanguage();
  const brand = useBrand();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const brokerCompany = user?.broker_company || null;
  // Deals + commission are financials: the owner always sees them; an employee
  // only if the owner granted broker.viewFinancials.
  const canFinancials = !!(user?.broker_owner || user?.permissions?.['broker.viewFinancials']);

  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [myDeals, setMyDeals] = useState([]);
  const [myDealsTotals, setMyDealsTotals] = useState({ count: 0, value: 0, commission: 0 });
  const [dealRequests, setDealRequests] = useState([]);
  const [showDealModal, setShowDealModal] = useState(false);
  const refreshDealRequests = () => brokerDealRequests().then((r) => { if (r && Array.isArray(r.requests)) setDealRequests(r.requests); });

  useEffect(() => {
    axios.get('/api/broker/projects')
      .then((res) => setProjects(res.data.projects || []))
      .catch((err) => console.error('broker/projects', err))
      .finally(() => setLoading(false));
    // Load broker-scoped deals only when the user may see financials.
    if (canFinancials) {
      axios.get('/api/broker/my-deals')
        .then((res) => {
          setMyDeals(res.data.deals || []);
          setMyDealsTotals(res.data.totals || { count: 0, value: 0, commission: 0 });
        })
        .catch((err) => console.error('broker/my-deals', err));
    }
    refreshDealRequests();
  }, []);

  useEffect(() => {
    if (!activeProject) return;
    setUnitsLoading(true);
    axios.get('/api/broker/units', { params: { project: activeProject } })
      .then((res) => setUnits(res.data.units || []))
      .catch((err) => console.error('broker/units', err))
      .finally(() => setUnitsLoading(false));
  }, [activeProject]);

  const totals = useMemo(() => {
    const init = { total: 0, available: 0, reserved: 0, sold: 0, offPlan: 0, value: 0 };
    return projects.reduce((acc, p) => {
      acc.total += p.total;
      acc.value += p.totalValue;
      acc.available += p.byStatus['متاح']?.count || 0;
      acc.reserved += p.byStatus['محجوز']?.count || 0;
      acc.sold += (p.byStatus['مباع']?.count || 0) + (p.byStatus['أفرغ']?.count || 0);
      acc.offPlan += p.byStatus['بيع على الخارطة']?.count || 0;
      return acc;
    }, init);
  }, [projects]);

  const sortedProjects = useMemo(
    () => sortProjects(projects.map((p) => p.project)).map((key) => projects.find((p) => p.project === key)).filter(Boolean),
    [projects]
  );

  const filteredUnits = useMemo(() => {
    if (!statusFilter) return units;
    return units.filter((u) => u.status === statusFilter);
  }, [units, statusFilter]);

  const portalTitle = lang === 'ar'
    ? `${brand.brandNameAr || brand.brandName} \u2014 بوابة الوسطاء`
    : `${brand.brandName} \u2014 Broker Portal`;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.4 }}>&#9203;</div>
        <div style={{ color: 'var(--text-secondary)' }}>{t('loading') || 'Loading...'}</div>
      </div>
    );
  }

  // ── Drill-down view ──
  if (activeProject) {
    const projectData = projects.find((p) => p.project === activeProject);
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div>
            <button
              onClick={() => { setActiveProject(null); setUnits([]); setStatusFilter(''); }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: 700,
                padding: 0,
                marginBottom: 4,
              }}
            >
              {lang === 'ar' ? '\u2190 كل المشاريع' : '\u2190 All Projects'}
            </button>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0 }}>
              {projLabel(activeProject, t)}
            </h2>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
            {projectData?.total || 0} {lang === 'ar' ? 'وحدة' : 'units'} \u00B7
            <span style={{ direction: 'ltr', marginInlineStart: 6 }}>{fmt(projectData?.totalValue || 0)} SAR</span>
          </div>
        </div>

        {/* Status filter chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <StatusPill
            active={!statusFilter}
            onClick={() => setStatusFilter('')}
            label={lang === 'ar' ? 'الكل' : 'All'}
            count={units.length}
            color="var(--accent)"
          />
          {STATUS_ORDER.map((s) => {
            const count = projectData?.byStatus[s]?.count || 0;
            if (count === 0) return null;
            return (
              <StatusPill
                key={s}
                active={statusFilter === s}
                onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                label={stLabel(s, lang)}
                count={count}
                color={ST_COLORS[s]}
              />
            );
          })}
        </div>

        {unitsLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            {t('loading') || 'Loading...'}
          </div>
        ) : isMobile ? (
          <MobileUnits rows={filteredUnits} t={t} lang={lang} />
        ) : (
          <DesktopUnits rows={filteredUnits} t={t} lang={lang} />
        )}
      </div>
    );
  }

  // ── Landing view: portfolio overview + project cards ──
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>
          {portalTitle}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '1.7rem', fontWeight: 800, margin: 0 }}>
            {lang === 'ar' ? 'محفظة الوحدات' : 'Inventory Overview'}
          </h1>
          {brokerCompany && (
            <span
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                background: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.35)',
                color: '#f59e0b',
                fontSize: '0.9rem',
                fontWeight: 700,
              }}
            >
              {brokerCompany}
            </span>
          )}
          {brokerCompany && (
            <button
              className="btn btn-primary btn-sm"
              style={{ marginInlineStart: 'auto' }}
              onClick={() => setShowDealModal(true)}
            >
              {lang === 'ar' ? '➕ تسجيل صفقة جديدة' : '➕ Submit a deal'}
            </button>
          )}
        </div>
        <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: 6 }}>
          {lang === 'ar'
            ? 'استعرض كافة المشاريع والوحدات المتاحة. اضغط على أي مشروع لعرض التفاصيل.'
            : 'Browse all projects and available inventory. Click any project to view details.'}
        </div>
      </div>

      {/* Portfolio-wide KPIs — brokers don't see sold totals (kept internal) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
              gap: 8,
              marginBottom: 18,
            }}
          >
            <KpiTile label={lang === 'ar' ? 'إجمالي الوحدات' : 'Total Units'} value={totals.total} color="var(--accent)" />
            <KpiTile label={stLabel('متاح', lang)} value={totals.available} color={ST_COLORS['متاح']} />
            <KpiTile label={stLabel('محجوز', lang)} value={totals.reserved} color={ST_COLORS['محجوز']} />
            <KpiTile label={stLabel('بيع على الخارطة', lang)} value={totals.offPlan} color={ST_COLORS['بيع على الخارطة']} />
          </div>

          {/* My Deals — broker-scoped deals closed by this broker company */}
          {brokerCompany && canFinancials && (
            <MyDealsSection
              deals={myDeals}
              totals={myDealsTotals}
              brokerCompany={brokerCompany}
              lang={lang}
              isMobile={isMobile}
              t={t}
            />
          )}

          {/* Deal submissions awaiting / reviewed by admin */}
          {brokerCompany && dealRequests.length > 0 && (
            <DealRequestsSection requests={dealRequests} lang={lang} t={t} />
          )}

          {/* Project grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {sortedProjects.map((p) => (
              <ProjectCard
                key={p.project}
                project={p}
                onClick={() => setActiveProject(p.project)}
                t={t}
                lang={lang}
              />
            ))}
          </div>

      {sortedProjects.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
          {lang === 'ar' ? 'لا توجد مشاريع متاحة حالياً' : 'No projects available yet'}
        </div>
      )}

      {showDealModal && (
        <DealSubmitModal
          lang={lang}
          onClose={() => setShowDealModal(false)}
          onSubmitted={() => { setShowDealModal(false); refreshDealRequests(); }}
        />
      )}
    </div>
  );
}

function MyDealsSection({ deals, totals, brokerCompany, lang, isMobile, t }) {
  const fmtMoney = (n) => `${fmt(Number(n || 0))} ${lang === 'ar' ? 'ريال' : 'SAR'}`;
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return '—'; }
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>
          {lang === 'ar' ? 'صفقاتي' : 'My Deals'}
        </h2>
        <span style={{
          padding: '2px 8px',
          borderRadius: 999,
          background: 'rgba(245,158,11,0.12)',
          border: '1px solid rgba(245,158,11,0.35)',
          color: '#f59e0b',
          fontSize: '0.7rem',
          fontWeight: 700,
        }}>{brokerCompany}</span>
      </div>

      {/* My-Deals KPI strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: 8,
        marginBottom: 10,
      }}>
        <KpiTile label={lang === 'ar' ? 'عدد الصفقات' : 'Total Deals'} value={totals.count} color="var(--accent)" />
        <KpiTile label={lang === 'ar' ? 'إجمالي القيمة' : 'Total Value'} value={fmtMoney(totals.value)} color="#10b981" />
        <KpiTile label={lang === 'ar' ? 'إجمالي العمولة' : 'Total Commission'} value={fmtMoney(totals.commission)} color="#f59e0b" />
      </div>

      {deals.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 24,
          background: 'var(--bg-card)',
          border: '1px dashed var(--border)',
          borderRadius: 10,
          color: 'var(--text-secondary)',
          fontSize: '0.85rem',
        }}>
          {lang === 'ar' ? 'لا توجد صفقات بعد' : 'No deals yet'}
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deals.map((d) => (
            <div key={d.id} style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                  {projLabel(d.project, t)} · {d.unit_code}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                  {fmtDate(d.deal_date)}
                </div>
              </div>
              {(d.client_name || d.client_phone) && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  {d.client_name || '—'}{d.client_phone ? ` · ${d.client_phone}` : ''}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'القيمة' : 'Value'}</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{fmtMoney(d.actual_price || d.price)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'العمولة' : 'Commission'}</span>
                <span style={{ fontWeight: 700, color: '#f59e0b' }}>{fmtMoney(d.broker_amt)}{d.broker_pct ? ` (${d.broker_pct}%)` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'المشروع' : 'Project'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'الوحدة' : 'Unit'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'العميل' : 'Client'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'القيمة' : 'Value'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'العمولة' : 'Commission'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'القناة' : 'Channel'}</th>
                <th style={thStyle(lang)}>{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle(lang)}>{projLabel(d.project, t)}</td>
                  <td style={{ ...tdStyle(lang), fontWeight: 700 }}>{d.unit_code}</td>
                  <td style={tdStyle(lang)}>
                    <div>{d.client_name || '—'}</div>
                    {d.client_phone && (
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>{d.client_phone}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle(lang), fontWeight: 700 }}>{fmtMoney(d.actual_price || d.price)}</td>
                  <td style={{ ...tdStyle(lang), color: '#f59e0b', fontWeight: 700 }}>
                    {fmtMoney(d.broker_amt)}
                    {d.broker_pct ? <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginInlineStart: 4 }}>({d.broker_pct}%)</span> : null}
                  </td>
                  <td style={tdStyle(lang)}>{d.channel || '—'}</td>
                  <td style={tdStyle(lang)}>{fmtDate(d.deal_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = (lang) => ({
  padding: '8px 10px',
  textAlign: lang === 'ar' ? 'right' : 'left',
  fontSize: '0.74rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
});

const tdStyle = (lang) => ({
  padding: '10px',
  textAlign: lang === 'ar' ? 'right' : 'left',
  color: 'var(--text-primary)',
});

function ProjectCard({ project, onClick, t, lang }) {
  // Brokers don't see sold counts on the dashboard cards — that's revealed only
  // inside the project details (drill-down). Cards highlight what's available.
  const available = project.byStatus['متاح']?.count || 0;
  const reserved = project.byStatus['محجوز']?.count || 0;
  const offPlan = project.byStatus['بيع على الخارطة']?.count || 0;

  return (
    <button
      onClick={onClick}
      style={{
        textAlign: lang === 'ar' ? 'right' : 'left',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 14,
        cursor: 'pointer',
        transition: 'all 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)' }}>
            {projLabel(project.project, t)}
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 3 }}>
            {project.total} {lang === 'ar' ? 'وحدة' : 'units'}
          </div>
        </div>
        <div
          style={{
            background: available > 0 ? '#22c55e' : '#94a3b8',
            color: '#fff',
            padding: '5px 12px',
            borderRadius: 12,
            fontSize: '0.82rem',
            fontWeight: 800,
            whiteSpace: 'nowrap',
          }}
        >
          {available} {lang === 'ar' ? 'متاح' : 'available'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        <MiniStat label={stLabel('متاح', lang)} value={available} color={ST_COLORS['متاح']} />
        <MiniStat label={stLabel('محجوز', lang)} value={reserved} color={ST_COLORS['محجوز']} />
        <MiniStat label={offPlan > 0 ? stLabel('بيع على الخارطة', lang) : '—'} value={offPlan} color={ST_COLORS['بيع على الخارطة']} />
      </div>
    </button>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 6px', background: color + '12', borderRadius: 6 }}>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function KpiTile({ label, value, color }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderInlineStart: '3px solid ' + color,
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 8, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1.1, direction: 'ltr' }}>{value}</div>
    </div>
  );
}

function DesktopUnits({ rows, t, lang }) {
  if (rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
        {lang === 'ar' ? 'لا توجد وحدات بهذه الحالة' : 'No units in this status'}
      </div>
    );
  }
  // Brokers never need to see earnest money — it's a deal-internal field that
  // leaks information about closed transactions. Show price only for available
  // units (taken units display '—' so the broker just sees "this one is gone").
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="data-table-wrapper" style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
        <table className="data-table" style={{ fontSize: '0.95rem' }}>
          <thead>
            <tr>
              <th style={stickyTh}>{t('udUnit')}</th>
              <th style={stickyTh}>{t('udType')}</th>
              <th style={stickyTh}>{t('udNeighborhood')}</th>
              <th style={{ ...stickyTh, textAlign: 'left' }}>{t('udPrice')}</th>
              <th style={stickyTh}>{t('udStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const taken = u.status === 'محجوز' || u.status === 'مباع' || u.status === 'أفرغ';
              return (
                <tr key={u.id}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{u.unit_code}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{u.unit_type || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{u.neighborhood || '—'}</td>
                  <td style={{ direction: 'ltr', fontWeight: 700, textAlign: 'left', color: taken ? 'var(--text-secondary)' : 'inherit' }}>
                    {taken ? '—' : fmt(u.current_price || u.base_price)}
                  </td>
                  <td><StatusBadge status={u.status} lang={lang} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileUnits({ rows, t, lang }) {
  if (rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
        {lang === 'ar' ? 'لا توجد وحدات بهذه الحالة' : 'No units in this status'}
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {rows.map((u, i) => {
        const taken = u.status === 'محجوز' || u.status === 'مباع' || u.status === 'أفرغ';
        return (
          <div
            key={u.id}
            style={{
              padding: '12px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', letterSpacing: 0.3, marginBottom: 2 }}>
                  {u.unit_type || ''} {u.neighborhood ? '\u00B7 ' + u.neighborhood : ''}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 800 }}>{u.unit_code}</div>
              </div>
              <StatusBadge status={u.status} lang={lang} />
            </div>
            {!taken && (
              <div style={{ fontSize: '0.92rem', marginTop: 6 }}>
                <span style={{ direction: 'ltr', fontWeight: 800, color: 'var(--accent)' }}>
                  {fmt(u.current_price || u.base_price)} SAR
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status, lang }) {
  const color = ST_COLORS[status] || '#888';
  return (
    <span
      style={{
        padding: '4px 11px',
        borderRadius: 12,
        fontSize: '0.85rem',
        fontWeight: 700,
        background: color + '18',
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {stLabel(status, lang)}
    </span>
  );
}

function StatusPill({ active, onClick, label, count, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 12px',
        borderRadius: 14,
        border: active ? '1.5px solid ' + color : '1px solid var(--border)',
        background: active ? color : 'var(--bg-card)',
        color: active ? '#fff' : 'var(--text-secondary)',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span>{label}</span>
      <span
        style={{
          background: active ? 'rgba(255,255,255,0.25)' : color + '18',
          color: active ? '#fff' : color,
          padding: '0 6px',
          borderRadius: 8,
          fontSize: '0.66rem',
          minWidth: 18,
          textAlign: 'center',
          fontWeight: 800,
        }}
      >
        {count}
      </span>
    </button>
  );
}

const stickyTh = { position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1, whiteSpace: 'nowrap' };

// ── Broker deal submission ──
const dsOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const dsCard = { background: 'var(--bg-card)', borderRadius: 16, padding: 22, width: 'min(560px, 96vw)', maxHeight: '92vh', overflowY: 'auto', border: '1px solid var(--border)' };
const dsGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
const dsLbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' };
const dsInp = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' };
const dsCellH = { padding: '8px 12px', textAlign: 'start', fontWeight: 700, whiteSpace: 'nowrap' };
const dsCell = { padding: '8px 12px', textAlign: 'start' };

function DealSubmitModal({ lang, onClose, onSubmitted }) {
  const L = (a, e) => (lang === 'ar' ? a : e);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');
  const [units, setUnits] = useState([]);
  const [unitCode, setUnitCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [price, setPrice] = useState('');
  const [payment, setPayment] = useState('تحويل بنكي');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get('/api/broker/projects').then((r) => setProjects(r.data.projects || [])).catch(() => {});
  }, []);
  useEffect(() => {
    setUnitCode('');
    if (!project) { setUnits([]); return; }
    axios.get('/api/broker/units', { params: { project } })
      .then((r) => setUnits((r.data.units || []).filter((u) => ['متاح', 'بيع على الخارطة'].includes(u.status))))
      .catch(() => setUnits([]));
  }, [project]);

  const selectedUnit = units.find((u) => u.unit_code === unitCode);
  useEffect(() => {
    if (selectedUnit) setPrice(String(selectedUnit.current_price || selectedUnit.base_price || ''));
  }, [unitCode, selectedUnit]);

  const ERRORS = {
    unit_not_available: L('الوحدة لم تعد متاحة', 'Unit is no longer available'),
    request_already_pending: L('هناك طلب معلّق على هذه الوحدة', 'A request is already pending for this unit'),
    unit_not_found: L('الوحدة غير موجودة', 'Unit not found'),
    price_required: L('أدخل سعراً صحيحاً', 'Enter a valid price'),
    client_name_required: L('اسم العميل مطلوب', 'Client name is required'),
    project_and_unit_required: L('اختر المشروع والوحدة', 'Select a project and unit'),
  };

  const submit = async () => {
    setErr('');
    if (!project || !unitCode) { setErr(ERRORS.project_and_unit_required); return; }
    if (!clientName.trim()) { setErr(ERRORS.client_name_required); return; }
    if (!(Number(price) > 0)) { setErr(ERRORS.price_required); return; }
    setBusy(true);
    const res = await brokerSubmitDeal({
      project, unit_code: unitCode, client_name: clientName.trim(), client_phone: clientPhone.trim(),
      actual_price: Number(price), payment_method: payment, note: note.trim(),
    });
    setBusy(false);
    if (res && res.error) { setErr(ERRORS[res.error] || L('تعذّر إرسال الطلب', 'Could not submit the request')); return; }
    onSubmitted();
  };

  return (
    <div style={dsOverlay} onClick={onClose}>
      <div style={dsCard} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem' }}>{L('تسجيل صفقة جديدة', 'Submit a deal')}</h2>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 14 }}>
          {L('يُرسَل الطلب لاعتماد الإدارة قبل أن يُسجّل كصفقة.', 'Sent for admin approval before it becomes a registered deal.')}
        </div>
        <div style={dsGrid}>
          <label style={dsLbl}>{L('المشروع', 'Project')}
            <select value={project} onChange={(e) => setProject(e.target.value)} style={dsInp}>
              <option value="">{L('اختر مشروعاً', 'Select a project')}</option>
              {projects.map((p) => <option key={p.project} value={p.project}>{projLabel(p.project, lang)}</option>)}
            </select>
          </label>
          <label style={dsLbl}>{L('الوحدة', 'Unit')}
            <select value={unitCode} onChange={(e) => setUnitCode(e.target.value)} style={dsInp} disabled={!project}>
              <option value="">{units.length ? L('اختر وحدة متاحة', 'Select an available unit') : L('لا توجد وحدات متاحة', 'No available units')}</option>
              {units.map((u) => <option key={u.unit_code} value={u.unit_code}>{u.unit_code} — {u.unit_type || ''} — {fmt(Number(u.current_price || u.base_price || 0))}</option>)}
            </select>
          </label>
          <label style={dsLbl}>{L('اسم العميل', 'Client name')}
            <input value={clientName} onChange={(e) => setClientName(e.target.value)} style={dsInp} />
          </label>
          <label style={dsLbl}>{L('جوال العميل', 'Client phone')}
            <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} inputMode="tel" style={{ ...dsInp, direction: 'ltr' }} />
          </label>
          <label style={dsLbl}>{L('السعر (ريال)', 'Price (SAR)')}
            <input value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))} style={dsInp} inputMode="numeric" />
          </label>
          <label style={dsLbl}>{L('طريقة الدفع', 'Payment method')}
            <select value={payment} onChange={(e) => setPayment(e.target.value)} style={dsInp}>
              <option value="تحويل بنكي">{L('تحويل بنكي', 'Bank transfer')}</option>
              <option value="كاش">{L('كاش', 'Cash')}</option>
              <option value="تمويل عقاري">{L('تمويل عقاري', 'Mortgage')}</option>
            </select>
          </label>
        </div>
        <label style={{ ...dsLbl, marginTop: 10 }}>{L('ملاحظة (اختياري)', 'Note (optional)')}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...dsInp, minHeight: 60, resize: 'vertical' }} />
        </label>
        {err && <div style={{ color: '#ef4444', fontSize: '0.82rem', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>{L('إلغاء', 'Cancel')}</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>{busy ? '…' : L('إرسال للاعتماد', 'Submit for approval')}</button>
        </div>
      </div>
    </div>
  );
}

function DealRequestsSection({ requests, lang, t }) {
  const L = (a, e) => (lang === 'ar' ? a : e);
  const STAT = {
    pending: { bg: 'rgba(245,158,11,0.14)', color: '#f59e0b', label: L('قيد المراجعة', 'Pending') },
    approved: { bg: 'rgba(16,185,129,0.14)', color: '#10b981', label: L('معتمدة', 'Approved') },
    rejected: { bg: 'rgba(239,68,68,0.14)', color: '#ef4444', label: L('مرفوضة', 'Rejected') },
  };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: '0.95rem', fontWeight: 800, marginBottom: 8 }}>{L('صفقاتي المُرسلة', 'My submitted deals')}</div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ color: 'var(--text-secondary)' }}>
              <th style={dsCellH}>{L('المشروع / الوحدة', 'Project / Unit')}</th>
              <th style={dsCellH}>{L('العميل', 'Client')}</th>
              <th style={dsCellH}>{L('السعر', 'Price')}</th>
              <th style={dsCellH}>{L('الحالة', 'Status')}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => {
              const s = STAT[r.status] || STAT.pending;
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={dsCell}>{projLabel(r.project, t)} — {r.unit_code}</td>
                  <td style={dsCell}>{r.clientName || '—'}</td>
                  <td style={dsCell}>{fmt(Number(r.price || 0))}</td>
                  <td style={dsCell}>
                    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 999, fontWeight: 700, fontSize: '0.72rem' }}>{s.label}</span>
                    {r.status === 'rejected' && r.rejectReason ? <span style={{ color: 'var(--text-secondary)', marginInlineStart: 6 }}>— {r.rejectReason}</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
